import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import RichTextEditor from './RichTextEditor';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import {
  Share2,
  Users,
  Search,
  Send,
  Loader2,
  Mail,
  UserCheck,
  X,
  Info
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ShareCampaignModal = ({ 
  open, 
  onOpenChange, 
  campaign, 
  onShareSuccess 
}) => {
  const { token, user } = useAuth();
  const { isDarkMode } = useTheme();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [instructions, setInstructions] = useState('');
  const [loading, setLoading] = useState(false);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    if (open && campaign) {
      fetchUsers();
      setSelectedUsers([]);
      setInstructions('');
      setSearchTerm('');
    }
  }, [open, campaign]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = users.filter(u => 
        u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(users);
    }
  }, [searchTerm, users]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      // Fetch users from the same client
      const clientId = campaign?.client_id || user?.client_id;
      if (!clientId) {
        toast.error('Impossible de récupérer les utilisateurs');
        return;
      }

      const response = await axios.get(`${API_URL}/users?client_id=${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter out current user
      const otherUsers = (response.data || []).filter(u => u.id !== user?.id);
      setUsers(otherUsers);
      setFilteredUsers(otherUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
      // Fallback: create mock users if API fails
      setUsers([]);
      setFilteredUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleShare = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Veuillez sélectionner au moins un utilisateur');
      return;
    }

    setSharing(true);
    try {
      const response = await axios.post(
        `${API_URL}/v2/campaigns/${campaign.id}/share`,
        {
          user_ids: selectedUsers,
          instructions: instructions,
          send_email: true,
          send_notification: true
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success(`Campagne partagée avec ${selectedUsers.length} utilisateur(s)`);
      
      if (onShareSuccess) {
        onShareSuccess(response.data);
      }
      
      onOpenChange(false);
    } catch (error) {
      console.error('Error sharing campaign:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du partage');
    } finally {
      setSharing(false);
    }
  };

  const getSelectedUserNames = () => {
    return users
      .filter(u => selectedUsers.includes(u.id))
      .map(u => u.name)
      .join(', ');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`sm:max-w-[600px] max-h-[90vh] overflow-hidden ${isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}`}>
        <DialogHeader>
          <DialogTitle className={`flex items-center gap-2 ${isDarkMode ? 'text-white' : ''}`}>
            <Share2 className="h-5 w-5 text-indigo-500" />
            Partager la campagne
          </DialogTitle>
          <DialogDescription className={isDarkMode ? 'text-slate-400' : ''}>
            {campaign?.name || 'Sans titre'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* User Selection */}
          <div className="space-y-2">
            <Label className={`flex items-center gap-2 ${isDarkMode ? 'text-slate-200' : ''}`}>
              <Users className="h-4 w-4" />
              Sélectionner les destinataires
            </Label>
            
            {/* Search */}
            <div className="relative">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`} />
              <Input
                placeholder="Rechercher un utilisateur..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`pl-10 ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}`}
              />
            </div>

            {/* Selected users badges */}
            {selectedUsers.length > 0 && (
              <div className="flex flex-wrap gap-2 py-2">
                {users.filter(u => selectedUsers.includes(u.id)).map(u => (
                  <Badge 
                    key={u.id} 
                    variant="secondary"
                    className={`py-1 pl-2 pr-1 flex items-center gap-1 ${isDarkMode ? 'bg-indigo-600/20 text-indigo-300' : 'bg-indigo-100 text-indigo-700'}`}
                  >
                    <UserCheck className="h-3 w-3" />
                    {u.name}
                    <button 
                      onClick={() => toggleUserSelection(u.id)}
                      className="ml-1 p-0.5 rounded hover:bg-indigo-500/30"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}

            {/* User list */}
            <ScrollArea className={`h-40 border rounded-lg ${isDarkMode ? 'border-[hsl(220,14%,25%)]' : 'border-slate-200'}`}>
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className={`flex flex-col items-center justify-center h-full ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                  <Users className="h-8 w-8 mb-2 opacity-50" />
                  <p className="text-sm">Aucun utilisateur trouvé</p>
                </div>
              ) : (
                <div className="p-2 space-y-1">
                  {filteredUsers.map((u) => (
                    <div
                      key={u.id}
                      onClick={() => toggleUserSelection(u.id)}
                      className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                        selectedUsers.includes(u.id)
                          ? (isDarkMode ? 'bg-indigo-600/20' : 'bg-indigo-50')
                          : (isDarkMode ? 'hover:bg-[hsl(220,14%,20%)]' : 'hover:bg-slate-50')
                      }`}
                    >
                      <Checkbox
                        checked={selectedUsers.includes(u.id)}
                        onCheckedChange={() => toggleUserSelection(u.id)}
                        className="data-[state=checked]:bg-indigo-600"
                      />
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          {u.name}
                        </p>
                        <p className={`text-xs truncate ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          {u.email}
                        </p>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {u.role === 'admin' ? 'Admin' : 'Utilisateur'}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Instructions */}
          <div className="space-y-2">
            <Label className={`flex items-center gap-2 ${isDarkMode ? 'text-slate-200' : ''}`}>
              <Mail className="h-4 w-4" />
              Instructions (optionnel)
            </Label>
            <RichTextEditor
              content={instructions}
              onChange={setInstructions}
              placeholder="Ajoutez des instructions ou commentaires pour les destinataires..."
              minHeight="120px"
            />
          </div>

          {/* Info box */}
          <div className={`flex items-start gap-3 p-3 rounded-lg ${isDarkMode ? 'bg-indigo-600/10 border border-indigo-500/20' : 'bg-indigo-50 border border-indigo-100'}`}>
            <Info className={`h-5 w-5 mt-0.5 flex-shrink-0 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-500'}`} />
            <div className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
              <p className="font-medium">Les destinataires recevront :</p>
              <ul className="mt-1 space-y-0.5 list-disc list-inside text-xs">
                <li>Un email avec les détails de la campagne</li>
                <li>Une notification dans leur interface</li>
                <li>Accès pour voir ou modifier selon leurs permissions</li>
              </ul>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className={isDarkMode ? 'border-[hsl(220,14%,25%)] text-slate-300' : ''}
            >
              Annuler
            </Button>
            <Button
              onClick={handleShare}
              disabled={sharing || selectedUsers.length === 0}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {sharing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Partage en cours...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Partager ({selectedUsers.length})
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareCampaignModal;
